// @ts-nocheck
/**
 * Unified Scroll Animation System
 * A single-observer approach that handles all scroll-based animations
 */

class ScrollAnimationManager {
  constructor(options = {}) {
    this.options = {
      rootMargin: options.rootMargin || "0px",
      threshold: options.threshold || 0.1,
      reducedMotionQuery: "(prefers-reduced-motion: reduce)",
      ...options,
    };

    // Single observer for all animations
    this.observer = null;

    // Animation type handlers
    this.animationTypes = new Map();

    // Tracked elements
    this.elements = new Map();

    // Zoom elements need scroll listener
    this.zoomElements = new Set();
    this.scrollListener = null;

    // Initialize
    this.init();
  }

  init() {
    // Check for reduced motion preference
    if (window.matchMedia(this.options.reducedMotionQuery).matches) {
      this.handleReducedMotion();
      return;
    }

    // Setup intersection observer
    this.setupObserver();

    // Register animation types
    this.registerAnimationTypes();

    // Find and setup all animated elements
    this.discoverElements();

    // Setup scroll listener for zoom animations
    this.setupScrollListener();
  }

  handleReducedMotion() {
    // For reduced motion, show all elements immediately
    const allElements = document.querySelectorAll("[data-animate]");
    allElements.forEach((el) => {
      el.classList.add("animate-visible");
      el.style.opacity = "1";
      el.style.transform = "none";
      el.style.visibility = "visible";
    });
  }

  setupObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = entry.target;
          const elementData = this.elements.get(element);

          if (!elementData) return;

          if (entry.isIntersecting) {
            this.triggerAnimation(element, elementData);
          } else {
            this.resetAnimation(element, elementData);
          }
        });
      },
      {
        rootMargin: this.options.rootMargin,
        threshold: this.options.threshold,
      }
    );
  }

  registerAnimationTypes() {
    // Fade animation
    this.animationTypes.set("fade", {
      setup: (element, config) => {
        element.style.opacity = "0";
        element.style.transition = `opacity ${config.duration}ms ${config.easing}`;
      },
      animate: (element, config) => {
        element.style.opacity = "1";
      },
      reset: (element, config) => {
        if (config.resetOnExit) {
          element.style.opacity = "0";
        }
      },
    });

    // Slide animation
    this.animationTypes.set("slide", {
      setup: (element, config) => {
        const direction = config.direction || "up";
        const distance = config.distance || 30;
        const transform = this.getSlideTransform(direction, distance);

        element.style.opacity = "0";
        element.style.transform = transform;
        element.style.transition = `opacity ${config.duration}ms ${config.easing}, transform ${config.duration}ms ${config.easing}`;
      },
      animate: (element, config) => {
        element.style.opacity = "1";
        element.style.transform = "translateX(0) translateY(0)";
      },
      reset: (element, config) => {
        if (config.resetOnExit) {
          element.style.opacity = "0";
          const direction = config.direction || "up";
          const distance = config.distance || 30;
          element.style.transform = this.getSlideTransform(direction, distance);
        }
      },
    });

    // Sequence animation
    this.animationTypes.set("sequence", {
      setup: (element, config) => {
        // Find all sequence children
        const children = element.querySelectorAll("[data-sequence]");
        const sortedChildren = Array.from(children).sort((a, b) => {
          return parseInt(a.dataset.sequence) - parseInt(b.dataset.sequence);
        });

        // Store sorted children
        config.children = sortedChildren;

        // Setup each child
        sortedChildren.forEach((child) => {
          child.style.opacity = "0";
          child.style.visibility = "hidden";

          const direction = config.direction || "up";
          const distance = config.distance || 20;
          child.style.transform = this.getSlideTransform(direction, distance);
          child.style.transition = `all ${config.duration}ms ${config.easing}`;
        });
      },
      animate: (element, config) => {
        const { children, delay = 100 } = config;

        children.forEach((child, index) => {
          setTimeout(() => {
            child.style.opacity = "1";
            child.style.visibility = "visible";
            child.style.transform = "translateX(0) translateY(0)";
          }, index * delay);
        });
      },
      reset: (element, config) => {
        if (config.resetOnExit) {
          const { children } = config;
          const direction = config.direction || "up";
          const distance = config.distance || 20;

          children.forEach((child) => {
            child.style.opacity = "0";
            child.style.visibility = "hidden";
            child.style.transform = this.getSlideTransform(direction, distance);
          });
        }
      },
    });

    // Cascade animation
    this.animationTypes.set("cascade", {
      setup: (element, config) => {
        // Find cascade siblings
        const parent = element.parentElement;
        const siblings = Array.from(parent.children).filter((child) =>
          child.dataset.animate?.includes("cascade")
        );

        // Calculate order based on cascade type
        const orders = config.centerOut
          ? this.calculateCenterOutOrders(siblings.length)
          : siblings.map((_, index) => index);

        // Store order for this element
        const elementIndex = siblings.indexOf(element);
        config.order = orders[elementIndex];

        // Setup element
        element.style.opacity = "0";
        const direction = config.direction || "up";
        const distance = config.distance || 30;
        element.style.transform = this.getSlideTransform(direction, distance);
        element.style.transition = `all ${config.duration}ms ${config.easing}`;
      },
      animate: (element, config) => {
        const delay = (config.order || 0) * (config.delay || 150);

        setTimeout(() => {
          element.style.opacity = "1";
          element.style.transform = "translateX(0) translateY(0)";
        }, delay);
      },
      reset: (element, config) => {
        if (config.resetOnExit) {
          element.style.opacity = "0";
          const direction = config.direction || "up";
          const distance = config.distance || 30;
          element.style.transform = this.getSlideTransform(direction, distance);
        }
      },
    });

    // Zoom animation (scroll-based)
    this.animationTypes.set("zoom", {
      setup: (element, config) => {
        this.zoomElements.add(element);
        element.style.transform = "scale(1)";
        element.style.transition = "transform 100ms ease-out";

        // Store config on element for scroll handler
        element._zoomConfig = config;
      },
      animate: (element, config) => {
        // Zoom is handled by scroll listener
        this.updateZoomElement(element, config);
      },
      reset: (element, config) => {
        element.style.transform = "scale(1)";
      },
    });
  }

  discoverElements(rootEl = document) {
    const animatedElements = rootEl.querySelectorAll("[data-animate]");

    animatedElements.forEach((element) => {
      const config = this.parseElementConfig(element);
      const animationType = config.type;

      if (!this.animationTypes.has(animationType)) {
        console.warn(`Unknown animation type: ${animationType}`);
        return;
      }

      // Store element data
      this.elements.set(element, config);

      // Setup the animation
      const handler = this.animationTypes.get(animationType);
      handler.setup(element, config);

      // Handle elements that start in viewport (e.g., hero sections)
      if (config.startsInViewport) {
        // Don't observe, just trigger animation after a delay
        setTimeout(() => {
          // Make element visible by removing the CSS display:none
          element.classList.add("starts-in-viewport-ready");
          // Trigger animation on next frame after display change
          requestAnimationFrame(() => {
            this.triggerAnimation(element, config);
          });
        }, config.startsInViewportDelay);
      } else if (animationType !== "zoom") {
        // Observe element (except zoom which uses scroll)
        this.observer.observe(element);
      } else {
        // For zoom, trigger immediately to set initial state
        this.updateZoomElement(element, config);
      }
    });
  }

  parseElementConfig(element) {
    const dataset = element.dataset;

    return {
      type: dataset.animate,
      duration: parseInt(dataset.duration) || 600,
      delay: dataset.delay !== undefined ? parseInt(dataset.delay) : 100,
      easing: dataset.easing || "ease-out",
      direction: dataset.direction || "up",
      distance: parseInt(dataset.distance) || 30,
      resetOnExit: dataset.resetOnExit !== "false",
      centerOut: dataset.cascadeCenterOut === "true",
      scaleAmount: parseFloat(dataset.scaleAmount) || 0.2,
      autoTrigger: dataset.autoTrigger !== "false",
      startsInViewport: dataset.startsInViewport === "true",
      startsInViewportDelay: parseInt(dataset.startsInViewportDelay) || 300,
    };
  }

  getSlideTransform(direction, distance) {
    switch (direction) {
      case "up":
        return `translateY(${distance}px)`;
      case "down":
        return `translateY(-${distance}px)`;
      case "left":
        return `translateX(${distance}px)`;
      case "right":
        return `translateX(-${distance}px)`;
      default:
        return `translateY(${distance}px)`;
    }
  }

  calculateCenterOutOrders(totalElements) {
    if (totalElements === 1) return [0];

    const orders = new Array(totalElements);
    orders[0] = 0; // First element always gets order 0

    const pairs = [];
    let left = 1;
    let right = totalElements - 1;

    while (left <= right) {
      if (left === right) {
        pairs.push([left]);
      } else {
        pairs.push([left, right]);
      }
      left++;
      right--;
    }

    pairs.forEach((pair, pairIndex) => {
      pair.forEach((elementIndex) => {
        orders[elementIndex] = pairIndex + 1;
      });
    });

    return orders;
  }

  triggerAnimation(element, config) {
    const handler = this.animationTypes.get(config.type);
    if (handler) {
      // Check if this animation type should use delay
      const useDelay = ["fade", "slide"].includes(config.type);
      const delay = useDelay && config.delay ? config.delay : 0;

      if (delay > 0) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            handler.animate(element, config);
          });
        }, delay);
      } else {
        requestAnimationFrame(() => {
          handler.animate(element, config);
        });
      }
    }
  }

  resetAnimation(element, config) {
    const handler = this.animationTypes.get(config.type);
    if (handler) {
      element.classList.remove("animate-visible");
      handler.reset(element, config);
    }
  }

  setupScrollListener() {
    if (this.zoomElements.size === 0) return;

    this.scrollListener = this.throttle(() => {
      this.zoomElements.forEach((element) => {
        const config = element._zoomConfig;
        if (config) {
          this.updateZoomElement(element, config);
        }
      });
    }, 16); // ~60fps

    window.addEventListener("scroll", this.scrollListener, { passive: true });
  }

  updateZoomElement(element, config) {
    const percentage = this.calculateScrollPercentage(element);
    const scaleAmount = config.scaleAmount / 100 || 0.002;
    const scale = 1 + scaleAmount * percentage;
    element.style.transform = `scale(${scale})`;
  }

  calculateScrollPercentage(element) {
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;
    const rect = element.getBoundingClientRect();
    const elementTop = rect.top + scrollY;
    const elementHeight = element.offsetHeight;

    if (elementTop > scrollY + viewportHeight) {
      return 0;
    } else if (elementTop + elementHeight < scrollY) {
      return 100;
    }

    const distance = scrollY + viewportHeight - elementTop;
    const percentage = distance / ((viewportHeight + elementHeight) / 100);
    return Math.max(0, Math.min(100, Math.round(percentage)));
  }

  throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  // Public methods
  refresh(rootEl = document) {
    // Re-discover elements (useful for dynamic content)
    this.discoverElements(rootEl);
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.scrollListener) {
      window.removeEventListener("scroll", this.scrollListener);
    }
    this.elements.clear();
    this.zoomElements.clear();
  }

  // Static method for easy initialization
  static init(options = {}) {
    return new ScrollAnimationManager(options);
  }
}

// Initialize on DOM ready
let scrollAnimationManager;

function initScrollAnimations() {
  scrollAnimationManager = ScrollAnimationManager.init();
  // Expose to window for web components
  window.scrollAnimationManager = scrollAnimationManager;
}

// Auto-initialize
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initScrollAnimations);
} else {
  initScrollAnimations();
}

// Shopify theme support
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    if (scrollAnimationManager) {
      scrollAnimationManager.refresh(event.target);
    }
  });

  document.addEventListener("shopify:section:reorder", () => {
    if (scrollAnimationManager) {
      scrollAnimationManager.refresh();
    }
  });
}

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = ScrollAnimationManager;
}
if (typeof window !== "undefined") {
  window.ScrollAnimationManager = ScrollAnimationManager;
}
